import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { MainState, Event, ChatSession, PaymentIntent } from './mainTypes';
import { mainAPI } from './mainAPI';

const initialState: MainState = {
  // Events
  events: [],
  currentEvent: null,
  eventsLoading: false,
  
  // Chat
  chatSessions: [],
  currentChatSession: null,
  chatLoading: false,
  
  // Payments
  paymentIntents: [],
  currentPaymentIntent: null,
  paymentLoading: false,
  
  // Global
  error: null,
};

const mainSlice = createSlice({
  name: 'main',
  initialState,
  reducers: {
    setCurrentEvent: (state, action: PayloadAction<Event | null>) => {
      state.currentEvent = action.payload;
    },
    setCurrentChatSession: (state, action: PayloadAction<ChatSession | null>) => {
      state.currentChatSession = action.payload;
    },
    setCurrentPaymentIntent: (state, action: PayloadAction<PaymentIntent | null>) => {
      state.currentPaymentIntent = action.payload;
    },
    clearMainError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Get All Events
    builder.addMatcher(
      mainAPI.endpoints.getAllEvents.matchPending,
      (state) => {
        state.eventsLoading = true;
        state.error = null;
      }
    );
    builder.addMatcher(
      mainAPI.endpoints.getAllEvents.matchFulfilled,
      (state, action) => {
        state.eventsLoading = false;
        state.events = action.payload;
      }
    );
    builder.addMatcher(
      mainAPI.endpoints.getAllEvents.matchRejected,
      (state, action: any) => {
        state.eventsLoading = false;
        state.error = action.error?.message || 'Failed to fetch events';
      }
    );
    
    // Get Chat Sessions
    builder.addMatcher(
      mainAPI.endpoints.getChatSessions.matchFulfilled,
      (state, action) => {
        state.chatSessions = action.payload;
      }
    );
    
    // Send Chat Message
    builder.addMatcher(
      mainAPI.endpoints.sendChatMessage.matchPending,
      (state) => {
        state.chatLoading = true;
      }
    );
    builder.addMatcher(
      mainAPI.endpoints.sendChatMessage.matchFulfilled,
      (state, action) => {
        state.chatLoading = false;
        // Update current session with new message
        if (state.currentChatSession?.session_id === action.payload.session_id) {
          state.currentChatSession.messages.push(action.payload.message);
        }
      }
    );
    builder.addMatcher(
      mainAPI.endpoints.sendChatMessage.matchRejected,
      (state) => {
        state.chatLoading = false;
      }
    );
    
    // Create Payment Intent
    builder.addMatcher(
      mainAPI.endpoints.createPaymentIntent.matchFulfilled,
      (state, action) => {
        state.currentPaymentIntent = action.payload;
        state.paymentIntents.unshift(action.payload);
      }
    );
  },
});

export const {
  setCurrentEvent,
  setCurrentChatSession,
  setCurrentPaymentIntent,
  clearMainError,
} = mainSlice.actions;

export default mainSlice.reducer;
